import { useId } from '@chakra-ui/react';
import { type InitialRulesValues } from 'contexts/RulesDetailModalContext';
import { Form, useFormikContext } from 'formik';
import { useCallback, useMemo, useState } from 'react';

import {
  FILTER_UNITS,
  PartFileUnits,
  type ResolutionConfigMap,
  type RuleDetailsError,
} from '@frontend/api';
import {
  ModalBody,
  ModalFooter,
  ModalButtonGroup,
  VStack,
  HStack,
  FormLabel,
  FormControl,
  FormErrorMessage,
  Input,
  ShareExternalIcon,
  Heading,
  Tooltip,
  Divider,
  Text,
  Spinner,
  Center,
  Popconfirm,
  Button,
} from '@frontend/components';

import { hasUnitBearingFilters } from '../utils/dimensionalFilters';
import { AssigneeContainer } from './AssigneeContainer';
import { HelpMessage } from './HelpMessage';
import { ResolutionsContainer } from './ResolutionsContainer';
import { RulePreviewItem } from './RulePreviewItem';
import { type FormStateData } from './RulesDetailModal';
import { SignalsContainer } from './SignalsContainer';
import { UnitPicker } from './UnitPicker';

type RulesDetailModalContentProps = {
  isLoadingConfig: boolean;
  isReadOnly: boolean;
  mutationError: RuleDetailsError | null;
  isMutating: boolean;
  resolutionConfigMapped: ResolutionConfigMap | null;
  initialRule?: InitialRulesValues;
  showConfigureRulesButton?: boolean;
  submitButtonText: string;
  onClose: () => void;
  deleteRule: (params: { uuid: string }) => void;
  mode: 'create' | 'update' | 'validate';
  initialFocusRef: React.RefObject<HTMLInputElement>;
};

export const RulesDetailModalContent = ({
  isLoadingConfig,
  isReadOnly,
  mutationError,
  isMutating,
  resolutionConfigMapped,
  initialRule,
  showConfigureRulesButton,
  submitButtonText,
  onClose,
  deleteRule,
  mode,
  initialFocusRef,
}: RulesDetailModalContentProps) => {
  const { values, touched, errors, getFieldProps, isValid, setFieldValue } =
    useFormikContext<FormStateData>();
  const signals = values.signals;

  const combinedErrors = useMemo(() => {
    const out = { ...errors };
    if (mutationError && mutationError?.extra?.name?.[0]) {
      out.name = mutationError.extra.name[0];
    }
    return out;
  }, [errors, mutationError]);

  const formId = useId(undefined, 'rules-detail-form');
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);

  // The picker holds an in/mm choice that drives both dimensional and
  // roughness queries (the suffix layer renders 'in' as 'µin' for roughness
  // inputs). NOTE: this assumes that all queries in a signal have the same units.
  const existingSignalDimensionalUnits = useMemo(() => {
    const unitsSet = new Set<PartFileUnits>();
    signals.forEach((signal) => {
      signal.groups.forEach((group) => {
        group.queries.forEach((query) => {
          if (
            query.units &&
            (query.units === FILTER_UNITS.IN || query.units === FILTER_UNITS.MM)
          ) {
            unitsSet.add(query.units);
          }
        });
      });
    });
    if (unitsSet.size === 1) {
      return Array.from(unitsSet)[0];
    } else {
      return FILTER_UNITS.IN;
    }
  }, [signals]);

  const [signalUnits, setSignalUnits] = useState<PartFileUnits>(
    existingSignalDimensionalUnits,
  );

  const signalHasUnitFilters = useMemo(
    () => signals.some(hasUnitBearingFilters),
    [signals],
  );

  const handleUnitChange = useCallback(
    (unit: PartFileUnits) => {
      setSignalUnits(unit);
      const nextSignals = [...signals];
      nextSignals.forEach((signal) => {
        signal.groups.forEach((group) => {
          group.queries.forEach((query) => {
            if (query.units) {
              query.units = unit;
            }
          });
        });
      });
      setFieldValue('signals', nextSignals);
    },
    [signals, setFieldValue],
  );

  return (
    <>
      <ModalBody
        p={0}
        flex={1}
        minHeight={0}
        display="flex"
        overflow="hidden"
        as={Form}
        id={formId}
        noValidate
        onKeyDown={(e) => {
          // Prevent form submission on Enter key
          // TODO: previously form submission was causing the signals to be removed
          //  and resolutions to intermittently disappear. Likely need to fix this in
          //  the way we pipe fields into formik and reset on validation.
          if (e.key === 'Enter') {
            e.preventDefault();
            e.stopPropagation();
          }
        }}
        className="rules-detail-modal-body"
      >
        {isLoadingConfig ? (
          <Center padding={2} flexGrow={1} className="rules-detail-loading">
            <Spinner />
          </Center>
        ) : (
          <>
            {/* Rule Editor*/}
            <VStack
              gap={2}
              align="stretch"
              borderRight="1px solid"
              borderColor="separator.border"
              p={2}
              overflowY="auto"
              flex="1 1 100%"
              className="rules-detail-editor"
            >
              <FormControl
                isInvalid={Boolean(touched.name && combinedErrors.name)}
                isRequired
                className="rules-detail-name-control"
              >
                <FormLabel>Name</FormLabel>
                <Input
                  {...getFieldProps('name')}
                  ref={initialFocusRef}
                  autoComplete="off"
                  isDisabled={isReadOnly}
                  className="rules-detail-name-input"
                />
                <FormErrorMessage>
                  {String(combinedErrors.name ?? '')}
                </FormErrorMessage>
              </FormControl>
              <FormControl
                isInvalid={Boolean(
                  touched.description && combinedErrors.description,
                )}
                className="rules-detail-description-control"
              >
                <FormLabel>Description</FormLabel>
                <Input
                  {...getFieldProps('description')}
                  autoComplete="off"
                  isDisabled={isReadOnly}
                  className="rules-detail-description-input"
                />
                <FormErrorMessage>
                  {String(combinedErrors.description ?? '')}
                </FormErrorMessage>
              </FormControl>
              <Divider className="rules-detail-divider" />
              {/* Signals */}
              <VStack
                gap={1}
                align="stretch"
                className="rules-detail-signals-section"
              >
                <HelpMessage
                  title={
                    <HStack justify="space-between" align="center" width="100%">
                      <Heading size="md">Signals</Heading>
                      {signalHasUnitFilters && (
                        <UnitPicker
                          value={signalUnits}
                          onChange={handleUnitChange}
                          isDisabled={isReadOnly}
                        />
                      )}
                    </HStack>
                  }
                >
                  <VStack gap={0.5} align="start">
                    <Text size="md">
                      Add signals to indicate when this action should appear.
                    </Text>
                  </VStack>
                </HelpMessage>
                <SignalsContainer
                  isReadOnly={isReadOnly}
                  logicalOperator={values.logicalOperator}
                  signalUnits={signalUnits}
                />
              </VStack>
              <Divider className="rules-detail-divider" />
              {/* Resolutions */}
              <VStack
                spacing={1}
                flex={1}
                align="stretch"
                className="rules-detail-resolutions-section"
              >
                <HelpMessage
                  title={<Heading size="md">Resolution Options</Heading>}
                >
                  <Text size="md">
                    Add resolution options to enable one-click actions in
                    response to content found in PDFs.
                  </Text>
                </HelpMessage>
                <ResolutionsContainer isReadOnly={isReadOnly} />
              </VStack>
              <Divider className="rules-detail-divider" />
              {/* Assignee */}
              <VStack
                spacing={1}
                flex={1}
                align="stretch"
                className="rules-detail-assignee-section"
              >
                <HelpMessage title={<Heading size="md">Assignee</Heading>}>
                  <Text size="md">
                    An assignee will receive a notification whenever this action
                    appears.
                  </Text>
                </HelpMessage>
                <AssigneeContainer isReadOnly={isReadOnly} />
              </VStack>
            </VStack>
            {/* FiF Sidebar and Rules Preview */}
            <VStack
              flexBasis={46}
              flexShrink={0}
              flexGrow={0}
              align="stretch"
              backgroundColor="page.plateLight"
              justify="flex-start"
              className="rules-detail-preview-sidebar"
            >
              {/* TODO: FiF */}
              <VStack
                gap={1}
                flex="0 0 auto"
                p={2}
                alignItems="stretch"
                className="rules-detail-preview-container"
              >
                <Heading size="md">Preview</Heading>
                {resolutionConfigMapped && (
                  <RulePreviewItem
                    rule={values}
                    configMap={resolutionConfigMapped}
                  />
                )}
              </VStack>
            </VStack>
          </>
        )}
      </ModalBody>
      <ModalFooter>
        {(initialRule || showConfigureRulesButton) && (
          <ModalButtonGroup flexGrow={1} justifyContent="start">
            {showConfigureRulesButton && (
              <Button
                as="a"
                fontFamily="inherit"
                fontSize="inherit"
                fontWeight="medium"
                href="/processes/rules"
                rel="noreferrer noopener"
                target="_blank"
                variant="link"
              >
                Configure Rules <ShareExternalIcon ml={0.5} />
              </Button>
            )}
            {initialRule?.uuid &&
              mode !== 'validate' &&
              (isReadOnly ? (
                <Tooltip label="You don't have permission to delete rules">
                  <Button
                    variant="link"
                    colorScheme="destructive"
                    size="lg"
                    isDisabled
                  >
                    Delete rule
                  </Button>
                </Tooltip>
              ) : (
                <Popconfirm
                  isOpen={isDeleteConfirmOpen}
                  onClose={() => setIsDeleteConfirmOpen(false)}
                  onConfirm={() =>
                    deleteRule({ uuid: initialRule?.uuid ?? '' })
                  }
                  confirmProps={{
                    children: 'Delete',
                    colorScheme: 'destructive',
                  }}
                  cancelProps={{
                    children: 'Cancel',
                  }}
                  title="Delete rule?"
                  contents={
                    <>
                      <Text>
                        Any action generated from this rule will still appear,
                        but no new actions will be created from this rule.
                      </Text>
                      <Text>This can&apos;t be undone.</Text>
                    </>
                  }
                >
                  <Button
                    variant="link"
                    colorScheme="destructive"
                    size="lg"
                    onClick={() => setIsDeleteConfirmOpen(true)}
                  >
                    Delete rule
                  </Button>
                </Popconfirm>
              ))}
          </ModalButtonGroup>
        )}
        <ModalButtonGroup flexGrow={1}>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          {isReadOnly ? (
            <Tooltip label="You don't have permission to edit rules">
              <Button type="submit" isDisabled form={formId}>
                {submitButtonText}
              </Button>
            </Tooltip>
          ) : (
            <Button
              type="submit"
              isDisabled={
                !isValid || isLoadingConfig || isMutating || !touched.name
              }
              form={formId}
              isLoading={isMutating}
            >
              {submitButtonText}
            </Button>
          )}
        </ModalButtonGroup>
      </ModalFooter>
    </>
  );
};
